import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faInfoCircle,
    faPlus,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import UnForm from "../../../../../Components/Form";
import { useHistory, useParams } from "react-router-dom";
import { ACESSOS } from "../../../../../Routes/Acessos";
import useAsyncCallback from "../../../../../hooks/useAsyncCallback";
import { SendTemplateTwilioController } from "../../../../../Controllers/HelpDesk/GestaoTemplate/GestaoTemplateController";
import {
    ITwillioBase,
    ITwillioQuickReplyType,
} from "../../../../../Interfaces/HelpDesk/GestaoTemplate/GestaoTemplateInterace";

interface ButtonState {
    id: number;
    buttonText: string;
    buttonId: string;
    variables: string[];
}

interface URLParams {
    name?: string;
    contentType?: string;
}

const tooltipMessage =
    "Clique aqui para adicionar variáveis neste campo. Variáveis permitem personalizar mensagens inserindo conteúdo dinâmico como nome de cliente ou URL única.";

// type quick_reply
export default function ContentType3() {
    const history = useHistory();

    const { name } = useParams<URLParams>();

    const [buttons, setButtons] = useState<ButtonState[]>([
        { id: 0, buttonText: "", buttonId: "", variables: [] },
    ]);

    const [bodyContent, setBodyContent] = useState("");
    const [variableCount, setVariableCount] = useState(0);
    const [variables, setVariables] = useState<{ [key: string]: string }>({});

    const [cursorPos, setCursorPos] = useState<number | null>(null);
    const [currentField, setCurrentField] = useState<
        | { field: "body" | "buttonText" | "buttonId"; index?: number }
        | undefined
    >();
    const [lastClickedButton, setLastClickedButton] = useState<
        "body" | "buttonText" | "buttonId" | undefined
    >(undefined);

    const handleBodyContentChangeBody = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        setBodyContent(e.target.value);
        setCurrentField({ field: "body" });
    };

    const handleFieldFocus = (
        field: "body" | "buttonText" | "buttonId",
        index: number | undefined,
        e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        setCurrentField({ field, index });
        setCursorPos(e.target.selectionStart);
    };

    const handleBodyContentChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        field: "buttonText" | "buttonId"
    ) => {
        const updatedButtons = [...buttons];
        updatedButtons[index][field] = e.target.value;
        setButtons(updatedButtons);
        setCurrentField({ field, index });
    };

    const insertVariable = () => {
        const newVariable = `{{${variableCount + 1}}}`;
        setVariableCount(variableCount + 1);
        setVariables({
            ...variables,
            [newVariable]: "",
        });

        if (currentField?.field === "body") {
            const textBeforeCursor = bodyContent.substring(0, cursorPos || 0);
            const textAfterCursor = bodyContent.substring(cursorPos || 0);
            setBodyContent(
                `${textBeforeCursor}${newVariable}${textAfterCursor}`
            );
        } else if (
            currentField?.field === "buttonText" &&
            currentField.index !== undefined
        ) {
            const index = currentField.index;
            const updatedButtons = [...buttons];
            const textBeforeCursor = updatedButtons[index].buttonText.substring(
                0,
                cursorPos || 0
            );
            const textAfterCursor = updatedButtons[index].buttonText.substring(
                cursorPos || 0
            );
            updatedButtons[
                index
            ].buttonText = `${textBeforeCursor}${newVariable}${textAfterCursor}`;
            setButtons(updatedButtons);
        } else if (
            currentField?.field === "buttonId" &&
            currentField.index !== undefined
        ) {
            const index = currentField.index;
            const updatedButtons = [...buttons];
            const textBeforeCursor = updatedButtons[index].buttonId.substring(
                0,
                cursorPos || 0
            );
            const textAfterCursor = updatedButtons[index].buttonId.substring(
                cursorPos || 0
            );
            updatedButtons[
                index
            ].buttonId = `${textBeforeCursor}${newVariable}${textAfterCursor}`;
            setButtons(updatedButtons);
        }
    };

    const handleInsertVariable = (
        field: "body" | "buttonText" | "buttonId",
        index?: number
    ) => {
        setCurrentField({ field, index });
        setLastClickedButton(field);
    };

    useEffect(() => {
        if (currentField && lastClickedButton) {
            insertVariable();
            setLastClickedButton(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentField, lastClickedButton]);

    const addButton = () => {
        setButtons([
            ...buttons,
            {
                id: buttons.length,
                buttonText: "",
                buttonId: "",
                variables: [],
            },
        ]);
    };

    const removeButton = (index: number) => {
        const updatedButtons = buttons.filter((_, i) => i !== index);
        setButtons(updatedButtons);
    };

    const removeVariable = (variable: string) => {
        const updatedVariables = { ...variables };
        delete updatedVariables[variable];
        setVariables(updatedVariables);

        if (bodyContent.includes(variable)) {
            const updatedBodyContent = bodyContent.replace(variable, "");
            setBodyContent(updatedBodyContent);
        }

        const updatedButtons = buttons.map((button) => {
            const updatedButtonText = button.buttonText.replace(variable, "");
            const updatedButtonId = button.buttonId.replace(variable, "");
            return {
                ...button,
                buttonText: updatedButtonText,
                buttonId: updatedButtonId,
            };
        });
        setButtons(updatedButtons);
    };

    const handleVariableChange = (
        key: string,
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setVariables({
            ...variables,
            [key]: e.target.value,
        });
    };

    const { execute: SendTemplateTwilio, loading: SendTemplateTwilioLoading } =
        useAsyncCallback(SendTemplateTwilioController);

    function handleQuickReply(formData: ITwillioBase<ITwillioQuickReplyType>) {
        const requestBody = {
            ...formData,
            friendly_name: name ?? "",
            language: "pt_BR",
            variables: variables,
        };

        const jsonString = JSON.stringify(requestBody);

        SendTemplateTwilio(jsonString, "QUICK_REPLY").then(() => {
            setBodyContent("");
            setButtons([
                { id: 0, buttonText: "", buttonId: "", variables: [] },
            ]);
            setVariables({});
            history.push(ACESSOS.HELPDESK.TELAS.GESTAO_TEMPLATES.DS_PATH);
        });
    }

    return (
        <UnForm handleSubmit={handleQuickReply}>
            <div>
                <label>Body</label>
                <UnForm.Textarea
                    name="types.twilio/quick-reply.body"
                    className="form form-control"
                    placeholder="Digite o conteúdo do body aqui"
                    value={bodyContent}
                    onChange={handleBodyContentChangeBody}
                    onFocus={(e) => handleFieldFocus("body", undefined, e)}
                    rows={4}
                    required
                />
                <div className="d-flex justify-content-end align-items-center mt-2">
                    <button
                        className="btn"
                        type="button"
                        onClick={() => handleInsertVariable("body")}
                    >
                        + Adicionar variável
                    </button>
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip-top">{tooltipMessage}</Tooltip>
                        }
                    >
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </OverlayTrigger>
                </div>
            </div>
            {buttons.map((button, index) => (
                <div className="card p-3 mb-3" key={button.id}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3>Button</h3>
                        </div>
                        <div>
                            {buttons.length > 1 && (
                                <div className="d-flex justify-content-end align-items-center mt-2">
                                    <button
                                        className="btn btn-outline-danger"
                                        type="button"
                                        onClick={() => removeButton(index)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />{" "}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-2">
                        <div>
                            <label>Button Text</label>
                            <UnForm.Input
                                name={`types.twilio/quick-reply.actions[${index}].title`}
                                className="form form-control"
                                placeholder="Digite o conteúdo do body aqui"
                                value={button.buttonText}
                                onChange={(e) =>
                                    handleBodyContentChange(
                                        e,
                                        index,
                                        "buttonText"
                                    )
                                }
                                onFocus={(e) =>
                                    handleFieldFocus("buttonText", index, e)
                                }
                                required
                            />
                            <div className="d-flex justify-content-between px-1">
                                <div className="text-muted">
                                    Suporta imagens, áudio, documentos e vídeo.
                                </div>
                            </div>
                            <div className="d-flex justify-content-end align-items-center mt-2">
                                <button
                                    className="btn"
                                    type="button"
                                    onClick={() =>
                                        handleInsertVariable(
                                            "buttonText",
                                            index
                                        )
                                    }
                                >
                                    + Adicionar variável
                                </button>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {tooltipMessage}
                                        </Tooltip>
                                    }
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </OverlayTrigger>
                            </div>
                        </div>
                        <div>
                            <label>ID</label>
                            <UnForm.Input
                                name={`types.twilio/quick-reply.actions[${index}].id`}
                                className="form form-control"
                                placeholder="Type ID"
                                value={button.buttonId}
                                onChange={(e) =>
                                    handleBodyContentChange(
                                        e,
                                        index,
                                        "buttonId"
                                    )
                                }
                                onFocus={(e) =>
                                    handleFieldFocus("buttonId", index, e)
                                }
                                required
                            />
                            <div className="d-flex justify-content-between px-1">
                                <div className="text-muted">
                                    Identificador único. Não visível para o
                                    destinatário.
                                </div>
                            </div>
                            <div className="d-flex justify-content-end align-items-center mt-2">
                                <button
                                    className="btn"
                                    type="button"
                                    onClick={() =>
                                        handleInsertVariable("buttonId", index)
                                    }
                                >
                                    + Adicionar variável
                                </button>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {tooltipMessage}
                                        </Tooltip>
                                    }
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </OverlayTrigger>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <div className="py-2">
                <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={addButton}
                >
                    Add button
                </button>
            </div>

            <div className="mt-3">
                {Object.keys(variables).map((variable, index) => (
                    <div key={index} className="mb-2">
                        <label>{variable}</label>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                value={variables[variable]}
                                onChange={(e) =>
                                    handleVariableChange(variable, e)
                                }
                                required
                            />
                            <div className="input-group-append">
                                <button
                                    className="btn btn-outline-danger"
                                    type="button"
                                    onClick={() => removeVariable(variable)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <hr className="my-5" />

            <div className="d-flex justify-content-start">
                <div>
                    <UnForm.SubmitButton
                        icon={faPlus}
                        disabled={SendTemplateTwilioLoading}
                        loading={SendTemplateTwilioLoading}
                        loadingSize="sm"
                        wrapperClassName="m-0"
                        color="success"
                    >
                        Criar
                    </UnForm.SubmitButton>
                </div>
                <div>
                    <button
                        className="btn btn-secondary ml-2"
                        type="button"
                        onClick={() => {
                            history.push(
                                ACESSOS.HELPDESK.TELAS.GESTAO_TEMPLATES_TWILIO
                                    .DS_PATH
                            );
                        }}
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </UnForm>
    );
}
